<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { heroData } from '../../config/pageData.js';
import { logEvent } from '@/utils/firebase.js';
import { getButtonUrl } from '@/utils/channelConfig.js';
import { getLogEventClickParams } from '@/utils/logEventParams';

const route = useRoute();
const ctaUrl = computed(() => getButtonUrl('topBtn', route.query.channel));

const scrollToCourse = () => {
  const el = document.getElementById('course');
  if (el) {
    window.scrollTo({ top: el.offsetTop - 48, behavior: 'smooth' });
  }
};
</script>

<template>
  <header class="m-hero">
    <div class="m-hero-inner">
      <div class="m-hero-tag">{{ heroData.tag }}</div>
      <h1 class="m-hero-title">
        {{ heroData.titleBefore }}
        <em>{{ heroData.titleHighlight }}</em>
        <br />
        {{ heroData.titleAfter }}
      </h1>
      <p class="m-hero-desc">{{ heroData.desc }}</p>
      <a
        :href="ctaUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="m-btn"
        @click="logEvent('mobile_fengsuids_topbtn_click', getLogEventClickParams())"
      >
        {{ heroData.ctaText }}
      </a>
      <a
        class="m-btn-ghost"
        href="#course"
        @click.prevent="scrollToCourse"
      >
        {{ heroData.secondaryText }}
      </a>
      <div class="m-hero-note">{{ heroData.note }}</div>
    </div>
    <div class="m-hero-photo">
      <img
        :src="heroData.cutout"
        :alt="heroData.cutoutAlt"
        class="m-hero-cutout"
      />
    </div>
  </header>
</template>

<style scoped lang="scss">
.m-hero {
  position: relative;
  background:
    radial-gradient(600px 380px at 70% 30%, rgba(201, 162, 94, 0.28), transparent 68%),
    linear-gradient(180deg, rgba(10, 18, 14, 0.82), rgba(10, 18, 14, 0.94)),
    url('../../../../assets/images/class3_wide.jpg') center / cover no-repeat;
  color: #f7f3ea;
  overflow: hidden;
}

.m-hero-inner {
  padding: 96px 32px 8px;
}

.m-hero-tag {
  color: #c9a25e;
  letter-spacing: 5px;
  font-size: 24px;
  margin-bottom: 20px;
}

.m-hero-title {
  font-size: 52px;
  line-height: 1.4;
  font-weight: 700;
  color: #fff;
  letter-spacing: 1px;

  em {
    font-style: normal;
    color: #c9a25e;
    border-bottom: 4px solid #c9a25e;
  }
}

.m-hero-desc {
  margin: 26px 0 32px;
  font-size: 28px;
  color: #e8e2d2;
}

.m-btn {
  display: block;
  width: fit-content;
  background: #c9a25e;
  color: #16241d;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 2px;
  border-radius: 999px;
  padding: 22px 56px;
  text-decoration: none;
}

.m-btn-ghost {
  display: block;
  width: fit-content;
  margin-top: 18px;
  color: #f7f3ea;
  font-size: 26px;
  letter-spacing: 2px;
  text-decoration: none;
  border: 2px solid rgba(201, 162, 94, 0.75);
  border-radius: 999px;
  padding: 16px 44px;
}

.m-hero-note {
  margin-top: 20px;
  font-size: 22px;
  color: #bdb6a4;
  text-align: center;
}

.m-hero-photo {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 24px 0 0;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 560px;
    height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(201, 162, 94, 0.3), transparent 62%);
    pointer-events: none;
  }
}

.m-hero-cutout {
  position: relative;
  height: 700px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.55));
}
</style>
